export const GROUP_BY = { NONE: "", EMPLOYEE: "employee", COST_CENTER: "cost_center", STORE: "store" };

export const USER_GROUP_OPTIONS = [
  { value: GROUP_BY.NONE, label: "None" },
  { value: GROUP_BY.EMPLOYEE, label: "Employee" },
  { value: GROUP_BY.COST_CENTER, label: "Cost Center" },
];

export const STORE_GROUP_OPTIONS = [
  { value: GROUP_BY.NONE, label: "None" },
  { value: GROUP_BY.STORE, label: "Store" },
];

export const isGrouped = (groupBy) =>
  groupBy === GROUP_BY.EMPLOYEE || groupBy === GROUP_BY.COST_CENTER || groupBy === GROUP_BY.STORE;

// Collapse state that survives paging: a mode ("everything collapsed" or
// "everything expanded") plus the groups the user toggled the other way.
// "Collapse all" therefore also applies to groups on pages not seen yet.
export const EXPANDED_ALL = Object.freeze({ allCollapsed: false, exceptions: new Set() });

export const isGroupCollapsed = (key, collapse = EXPANDED_ALL) =>
  collapse.allCollapsed ? !collapse.exceptions.has(key) : collapse.exceptions.has(key);

export const toggleGroupCollapse = (collapse, key) => {
  const exceptions = new Set(collapse.exceptions);
  if (exceptions.has(key)) exceptions.delete(key);
  else exceptions.add(key);
  return { allCollapsed: collapse.allCollapsed, exceptions };
};

export const collapseAllGroups = () => ({ allCollapsed: true, exceptions: new Set() });
export const expandAllGroups = () => ({ allCollapsed: false, exceptions: new Set() });

const headerRow = (group, level, collapsed) => ({
  __group: true,
  level,
  key: group.key,
  groupBy: group.group_by,
  employee: group.employee || null,
  costCenter: group.cost_center || null,
  store: group.store || null,
  count: group.document_count || 0,
  expired: group.expired_count || 0,
  attention: group.due_soon_count || 0,
  collapsed,
});

// Table rows from a page of the grouped API: [header, doc, doc, header, ...].
// A group may carry `subgroups` (cost centre -> employees); those become
// indented level-1 headers with their own collapse state. Documents of
// collapsed groups are left out; every header keeps its counts.
export const rowsFromGroups = (groups, collapse = EXPANDED_ALL) => {
  const rows = [];
  (groups || []).forEach((group) => {
    const collapsed = isGroupCollapsed(group.key, collapse);
    rows.push(headerRow(group, 0, collapsed));
    if (collapsed) return;
    if (group.subgroups?.length) {
      group.subgroups.forEach((sub) => {
        const subCollapsed = isGroupCollapsed(sub.key, collapse);
        rows.push(headerRow(sub, 1, subCollapsed));
        if (!subCollapsed) rows.push(...(sub.documents || []));
      });
    } else {
      rows.push(...(group.documents || []));
    }
  });
  return rows;
};

// Flat document list in page order (detail-page previous/next uses it).
export const flattenGroups = (groups) =>
  (groups || []).flatMap((group) =>
    group.subgroups?.length ? group.subgroups.flatMap((sub) => sub.documents || []) : group.documents || []
  );
